import { useReducer, useCallback, useEffect } from 'react'

const initState = {
  response: null,
  error: null,
  pending: false,
  complete: false,
}

const requestStateReducer = (state, { type, response, err }) => {
  switch (type) {
    case 'pending':
      return { ...state, pending: true, complete: false, error: null }
    case 'complete':
      // NOTE: Axios calls are wrapped in response.data. XMPP calls are just response.
      return { ...state, pending: false, complete: true, response: response?.data || response }
    case 'error':
      return { ...state, error: err, complete: true, pending: false }
    default:
      return state
  }
}

/**
 * Wraps an endpoint call with state related to the request.
 * @param {func} endpointFunction - Function that calls an endpoint internally. Must return a promise.
 * @returns [func, object] Returns the function and state. Use the returned function to keep the state in sync.
 */
export default function useRequestState(endpointFunction) {
  const [requestState, dispatch] = useReducer(requestStateReducer, initState)
  let isMounted = true
  const wrappedFunction = useCallback(
    (...args) => {
      dispatch({ type: 'pending' })
      return endpointFunction(...args)
        .then((response) => {
          isMounted && dispatch({ type: 'complete', response })
          return response
        })
        .catch((err) => {
          isMounted && dispatch({ type: 'error', err })
          return err
        })
    },
    [endpointFunction, isMounted]
  )
  useEffect(() => {
    return () => {
      isMounted = false
    }
  }, [])
  return [wrappedFunction, requestState]
}
